import React, { useEffect, useState } from "react";
import Box from "../../../kit/Box";
import TrackStatusCard from "../Component/TrackStatusCard";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import { Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTheme } from "@emotion/react";
import { updateAlertInfo } from "../../../redux/slice/userSlice";
import { useDispatch } from "react-redux";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import CancelIcon from "@mui/icons-material/Cancel";
import { rapidTrackStatus } from "../../../services/api";
import Loader from "../../../components/Loader/Loader";
import Popup from "../../../kit/Popup";

const contentKeyMapping = {
  INITIATED_BY: "Initiated By",
  INITIATED_ON: "Initiated On",
  PENDING_ON: "Pending On",
  PENDING_BY: "Pending By",
  REJECTED_BY: "Rejected By",
  REJECTED_ON: "Rejected On",
  COMMENTS: "Comments",
  ERROR_OCCURRED_ON: "Error Occurred On",
  ERROR: "Error",
  INFRA_PROVISIONING_FINISHED_ON: "Infra Provisioning Finished On",
};
const statusSteps = {
  initialised: {
    header: "Process Initialised",
    icon: <AccountTreeOutlinedIcon sx={{ fontSize: "28px" }} />,
    statusIcon: (
      <InfoIcon sx={{ fontSize: "20px", color: "gray", border: "none" }} />
    ),
    progressLine: "2px dashed gray",
    status: false,
    details: {},
  },
  costApproval: {
    header: "Cost Approval",
    icon: <PaymentsOutlinedIcon sx={{ fontSize: "28px" }} />,
    statusIcon: (
      <InfoIcon sx={{ fontSize: "20px", color: "gray", border: "none" }} />
    ),
    progressLine: "2px dashed gray",
    status: false,
    details: {},
  },
  infraSpawning: {
    header: "Infra Spawning",
    icon: <SettingsApplicationsIcon sx={{ fontSize: "28px" }} />,
    statusIcon: (
      <InfoIcon sx={{ fontSize: "20px", color: "gray", border: "none" }} />
    ),
    progressLine: "2px dashed gray",
    status: false,
    details: {},
  },
  accessToUsers: {
    header: "Access To Users",
    icon: <ManageAccountsIcon sx={{ fontSize: "28px" }} />,
    statusIcon: (
      <InfoIcon sx={{ fontSize: "20px", color: "gray", border: "none" }} />
    ),
    progressLine: "2px dashed gray",
    status: false,
    details: {},
  },
  finish: {
    header: "Process Finished",
    icon: <SportsScoreIcon sx={{ fontSize: "28px" }} />,
    statusIcon: (
      <InfoIcon sx={{ fontSize: "20px", color: "gray", border: "none" }} />
    ),
    progressLine: "2px dashed gray",
    status: false,
    details: {},
  },
};

const TrackStatus = ({ isRegistering = false, register_id }) => {
  const [status, setStatus] = useState(statusSteps);
  const [loader, setLoader] = useState(true);
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openDetail, setOpenDetail] = useState(false);
  const [detailsContent, setDetailsContent] = useState({});
  const handleOpen = () => setOpenDetail(true);

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(register_id)
      .then(() => {
        dispatch(
          updateAlertInfo({
            message: "Copied",
            open: true,
            severity: "success",
          })
        );
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };
  useEffect(() => {
    const fetchStatus = async () => {
      let current_status = {};
      try {
        const resp = await rapidTrackStatus({
          params: {
            register_id: register_id,
          },
        });

        const statusCode = resp?.data?.status?.statusCode;
        const message = resp?.data?.status?.statusMessage;
        if (statusCode === 200) {
          const response_data = resp.data?.data;
          for (let key in response_data) {
            if (response_data[key]?.is_current) {
              current_status = response_data[key];
            }
          }
          setLoader(false);
          const { status, content } = current_status;
          setDetailsContent(content);
          switch (status) {
            case "PROCESS_INITIATED":
              setStatus((prevStatus) => ({
                ...prevStatus,
                ["initialised"]: {
                  ...prevStatus["initialised"],
                  statusIcon: (
                    <CheckCircleIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                  status: true,
                  details: content,
                },
                ["costApproval"]: {
                  ...prevStatus["costApproval"],
                  status: true,
                  progressLine: `2px solid ${theme.palette.tertiary.yellow}`,
                },
              }));
              break;
            case "TECH_OWNER_RESPONSE_PENDING":
              setStatus((prevStatus) => ({
                ...prevStatus,
                ["initialised"]: {
                  ...prevStatus["initialised"],
                  statusIcon: (
                    <InfoIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  status: true,
                  progressLine: `2px solid ${theme.palette.tertiary.yellow}`,
                  details: content,
                },
              }));
              break;
            case "APPROVAL_PENDING":
              setStatus((prevStatus) => ({
                ...prevStatus,
                ["costApproval"]: {
                  ...prevStatus["costApproval"],
                  statusIcon: (
                    <InfoIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  status: true,
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                  details: content,
                },
                ["initialised"]: {
                  ...prevStatus["initialised"],
                  statusIcon: (
                    <CheckCircleIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                },
              }));
              break;
            case "INFRA_IN_PROGRESS":
              setDetailsContent({});
              setStatus((prevStatus) => ({
                ...prevStatus,
                ["costApproval"]: {
                  ...prevStatus["costApproval"],
                  statusIcon: (
                    <CheckCircleIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  status: true,
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                  details: "",
                },
                ["initialised"]: {
                  ...prevStatus["initialised"],
                  statusIcon: (
                    <CheckCircleIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                },
                ["infraSpawning"]: {
                  ...prevStatus["infraSpawning"],
                  statusIcon: (
                    <InfoIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                },
              }));
              break;
            case "APPROVAL_REJECTED":
              setStatus((prevStatus) => ({
                ...prevStatus,
                ["costApproval"]: {
                  ...prevStatus["costApproval"],
                  statusIcon: (
                    <CancelIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.red,
                        border: "none",
                      }}
                    />
                  ),
                  status: true,
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                  details: content,
                },
                ["initialised"]: {
                  ...prevStatus["initialised"],
                  statusIcon: (
                    <CheckCircleIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                },
              }));
              break;
            case "ERROR_WHILE_DEPLOYMENT":
              setStatus((prevStatus) => ({
                ...prevStatus,
                ["infraSpawning"]: {
                  ...prevStatus["infraSpawning"],
                  statusIcon: (
                    <CancelIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.red,
                        border: "none",
                      }}
                    />
                  ),
                  status: true,
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                  details: content,
                },
                ["costApproval"]: {
                  ...prevStatus["costApproval"],
                  statusIcon: (
                    <CheckCircleIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                },
                ["initialised"]: {
                  ...prevStatus["initialised"],
                  statusIcon: (
                    <CheckCircleIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                },
              }));
              break;
            case "FINISHED":
              setStatus((prevStatus) => ({
                ...prevStatus,
                ["finish"]: {
                  ...prevStatus["finish"],
                  statusIcon: (
                    <CheckCircleIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  status: true,
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                  details: content,
                },
                ["accessToUsers"]: {
                  ...prevStatus["accessToUsers"],
                  statusIcon: (
                    <CheckCircleIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                },
                ["infraSpawning"]: {
                  ...prevStatus["infraSpawning"],
                  statusIcon: (
                    <CheckCircleIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                },
                ["costApproval"]: {
                  ...prevStatus["costApproval"],
                  statusIcon: (
                    <CheckCircleIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                },
                ["initialised"]: {
                  ...prevStatus["initialised"],
                  statusIcon: (
                    <CheckCircleIcon
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.tertiary.green,
                        border: "none",
                      }}
                    />
                  ),
                  progressLine: `2px solid ${theme.palette.tertiary.green}`,
                },
              }));
              break;
            default:
          }
        } else {
          dispatch(
            updateAlertInfo({
              message: message,
              open: true,
              severity: "error",
            })
          );
        }
      } catch (err) {
        console.error("Error", err);
      }
    };
    fetchStatus();
  }, []);

  return (
    <>
      {!isRegistering && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: theme.size[8],
          }}
        >
          <div
            style={{
              marginBottom: theme.size[12],
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: theme.size[14],
                fontWeight: theme.fontWeight[500],
              }}
            >
              Request ID: {register_id}
            </Typography>
            <ContentCopyIcon
              sx={{
                marginLeft: theme.size[8],
                fontSize: theme.size[18],
                color: theme.palette.secondary.main,
                cursor: "pointer",
              }}
              onClick={copyToClipboard}
            />
          </div>
        </div>
      )}
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
          }}
        >
          {loader ? (
            <Loader />
          ) : (
            Object.keys(status).map((key, index) => (
              <React.Fragment key={key}>
                {index > 0 && (
                  <Box
                    style={{
                      flex: "0 1 auto",
                      borderTop: status[key].progressLine,
                      marginX: 1,
                      height: "2px",
                      width: "100%",
                    }}
                  />
                )}
                <TrackStatusCard
                  header={status[key].header}
                  stateIcon={status[key].icon}
                  statusIcon={status[key].statusIcon}
                  active={status[key].status}
                  details={status[key].details}
                  handleOpen={handleOpen}
                  style={{
                    flex: "1",
                    textAlign: "center",
                  }}
                />
              </React.Fragment>
            ))
          )}
        </Box>
      </Box>
      <Popup
        fullWidth={true}
        maxWidth={"sm"}
        backdropClickDisabled={true}
        disableEscapeKeyDown={false}
        showExpandBtn={true}
        customProps={{ contentTextSize: "22px", contentTextFontWeight: "450" }}
        contentText={"Details"}
        size={"24px"}
        content={
          <Box sx={{ mt: 2, padding: "0px 80px !important" }}>
            {Object.entries(detailsContent).map(([key, value]) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: "500",
                    mr: 1,
                    minWidth: "150px",
                  }}
                >
                  {contentKeyMapping[key]}:
                </Typography>
                {typeof value === "string" ? (
                  <Typography variant="body1" sx={{ fontWeight: "normal" }}>
                    {value}
                  </Typography>
                ) : Array.isArray(value) ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    {value.map((item, idx) => (
                      <Typography
                        key={idx}
                        variant="body1"
                        sx={{ fontWeight: "normal" }}
                      >
                        {typeof item === "string" && item}
                      </Typography>
                    ))}
                  </Box>
                ) : null}
              </Box>
            ))}
          </Box>
        }
        open={openDetail}
        setOpen={setOpenDetail}
      />
    </>
  );
};
export default TrackStatus;
